import { useMemo, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createLead, createTenant, getPlans, listTenants } from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { formatDateShort } from "../lib/format";
import type { CreateTenantResult, PlanRow, TenantRow, TenantStatus } from "../lib/types";
import { TopBar } from "../components/Layout";
import { StatusBadge } from "../components/Badge";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { Modal } from "../components/Modal";

const STATUSES: TenantStatus[] = [
  "provisioning",
  "active",
  "suspended",
  "deprovisioning",
  "deleted",
  "failed",
];

function isTenantStatus(v: string | null): v is TenantStatus {
  return !!v && (STATUSES as string[]).includes(v);
}

export function TenantsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [plan, setPlan] = useState("");
  const [sortDir, setSortDir] = useState<"desc" | "asc">("desc");
  const [showCreate, setShowCreate] = useState(false);

  // Status filter lives in the URL (?status=…) so dashboard KPIs can deep-link.
  const statusParam = searchParams.get("status");
  const status: TenantStatus | "" = isTenantStatus(statusParam) ? statusParam : "";
  function setStatus(next: TenantStatus | "") {
    setSearchParams(next ? { status: next } : {}, { replace: true });
  }

  // Load the whole registry once; search / status / plan / sort are client-side.
  const { data, loading, error, reload } = useApi((signal) => listTenants({}, signal), []);
  const plansApi = useApi((signal) => getPlans(signal), []);
  const plans = plansApi.data?.plans ?? [];
  const planName = useMemo(() => new Map(plans.map((p) => [p.id, p.name])), [plans]);

  const all = useMemo(() => data?.tenants ?? [], [data]);

  const countsByStatus = useMemo(() => {
    const counts = new Map<TenantStatus, number>();
    for (const t of all) counts.set(t.status, (counts.get(t.status) ?? 0) + 1);
    return counts;
  }, [all]);

  const tenants = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = all.filter(
      (t) =>
        (!status || t.status === status) &&
        (!plan || t.plan_id === plan) &&
        (!q || t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q)),
    );
    return [...filtered].sort((a, b) =>
      sortDir === "desc" ? b.created_at - a.created_at : a.created_at - b.created_at,
    );
  }, [all, search, status, plan, sortDir]);

  const filtersActive = Boolean(search.trim() || status || plan);

  return (
    <>
      <TopBar
        crumbs="Tenants"
        actions={
          <button className="btn primary" onClick={() => setShowCreate(true)}>
            + Create tenant
          </button>
        }
      />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Tenants</h1>
            <div className="sub">Control-plane registry of every provisioned workspace.</div>
          </div>
        </div>

        {/* Status summary doubles as the filter. */}
        <div className="chips" role="group" aria-label="Filter by status" style={{ marginBottom: 14 }}>
          <button
            type="button"
            className={`chip${status === "" ? " active" : ""}`}
            onClick={() => setStatus("")}
            aria-pressed={status === ""}
          >
            All <span className="count">{all.length}</span>
          </button>
          {STATUSES.filter((s) => (countsByStatus.get(s) ?? 0) > 0 || s === status).map((s) => (
            <button
              key={s}
              type="button"
              className={`chip${status === s ? " active" : ""}`}
              onClick={() => setStatus(status === s ? "" : s)}
              aria-pressed={status === s}
            >
              {s} <span className="count">{countsByStatus.get(s) ?? 0}</span>
            </button>
          ))}
        </div>

        <div className="toolbar">
          <label className="field grow">
            <span className="label">Search</span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by name or slug…"
            />
          </label>
          <label className="field">
            <span className="label">Plan</span>
            <select value={plan} onChange={(e) => setPlan(e.target.value)}>
              <option value="">All plans</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <button className="btn" onClick={reload} disabled={loading}>
            Refresh
          </button>
        </div>

        <div className="card">
          <div className="card-body tight">
            {loading ? (
              <Loading label="Loading tenants…" />
            ) : error ? (
              <ErrorState error={error} onRetry={reload} />
            ) : tenants.length === 0 ? (
              <EmptyState>
                {filtersActive
                  ? "No tenants match the current filters."
                  : "No tenants yet. Create the first one to kick off provisioning."}
              </EmptyState>
            ) : (
              <table className="data">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Slug</th>
                    <th>Status</th>
                    <th>Plan</th>
                    <th aria-sort={sortDir === "desc" ? "descending" : "ascending"}>
                      <button
                        type="button"
                        className="sortable"
                        onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                        title="Sort by created date"
                      >
                        Created {sortDir === "desc" ? "↓" : "↑"}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((t) => (
                    <tr
                      key={t.id}
                      className="clickable"
                      onClick={() => navigate(`/tenants/${t.id}`)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") navigate(`/tenants/${t.id}`);
                      }}
                    >
                      <td>
                        <strong>{t.name}</strong>
                      </td>
                      <td className="mono">{t.slug}</td>
                      <td>
                        <StatusBadge status={t.status} />
                      </td>
                      <td>
                        {t.plan_id ? (
                          <span className="pill">{planName.get(t.plan_id) ?? t.plan_id}</span>
                        ) : (
                          <span className="faint">—</span>
                        )}
                      </td>
                      <td className="num">{formatDateShort(t.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <p className="muted" style={{ marginTop: 10, fontSize: "0.8rem" }}>
          {!loading && !error
            ? `Showing ${tenants.length} of ${all.length} tenant${all.length === 1 ? "" : "s"}`
            : ""}
        </p>
      </div>

      {showCreate ? (
        <CreateTenantModal
          plans={plans}
          plansLoading={plansApi.loading}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function CreateTenantModal({
  plans,
  plansLoading,
  onClose,
  onCreated,
}: {
  plans: PlanRow[];
  plansLoading: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const navigate = useNavigate();

  // "lead" = save to the sales pipeline with NO provisioning (resources spun up
  // later via the Provision button); "provision" = create & provision now.
  const [mode, setMode] = useState<"lead" | "provision">("lead");
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [planCode, setPlanCode] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [result, setResult] = useState<CreateTenantResult | null>(null);
  const [leadResult, setLeadResult] = useState<TenantRow | null>(null);

  const provisionMut = useMutation(createTenant);
  const leadMut = useMutation(createLead);
  const pending = provisionMut.pending || leadMut.pending;
  const error = provisionMut.error ?? leadMut.error;

  const slugValid = useMemo(() => /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(slug), [slug]);
  // Provisioning needs an admin email; a lead can be saved with just slug+name.
  const canSubmit = !!slug && !!name && slugValid && (mode === "lead" || !!adminEmail);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    if (mode === "lead") {
      const res = await leadMut.run({
        slug: slug.trim(),
        name: name.trim(),
        ...(adminName.trim() ? { contactName: adminName.trim() } : {}),
        ...(adminEmail.trim() ? { contactEmail: adminEmail.trim() } : {}),
        ...(planCode ? { planCode } : {}),
        source: "manual",
      });
      if (res) setLeadResult(res.tenant);
      return;
    }
    const res = await provisionMut.run({
      slug: slug.trim(),
      name: name.trim(),
      adminEmail: adminEmail.trim(),
      adminName: adminName.trim() || "Admin",
      ...(planCode ? { planCode } : {}),
    });
    if (res) setResult(res);
  }

  if (leadResult) {
    return (
      <Modal
        title="Lead created"
        onClose={onCreated}
        footer={
          <>
            <button className="btn" onClick={onCreated}>
              Close
            </button>
            <button className="btn primary" onClick={() => navigate(`/tenants/${leadResult.id}`)}>
              Open lead
            </button>
          </>
        }
      >
        <Alert kind="success">
          Saved to the sales pipeline at stage <strong>New</strong>. No Cloudflare resources were
          created — use <strong>Provision</strong> on the lead once you're ready to spin it up.
        </Alert>
        <dl className="kv">
          <dt>Tenant ID</dt>
          <dd className="mono">{leadResult.id}</dd>
          <dt>Slug</dt>
          <dd className="mono">{leadResult.slug}</dd>
        </dl>
      </Modal>
    );
  }

  if (result) {
    const existed = result.alreadyExisted;
    return (
      <Modal
        title={existed ? "Tenant already exists" : "Provisioning started"}
        onClose={onCreated}
        footer={
          <>
            <button className="btn" onClick={onCreated}>
              Close
            </button>
            <button
              className="btn primary"
              onClick={() => navigate(`/tenants/${result.tenantId}`)}
            >
              Open tenant
            </button>
          </>
        }
      >
        <Alert kind={existed ? "info" : "success"}>
          {existed
            ? "A tenant with this slug already exists — returning the existing record."
            : "Provisioning has been enqueued. The tenant starts in “provisioning” status and flips to “active” once the pipeline finishes."}
        </Alert>
        <dl className="kv">
          <dt>Tenant ID</dt>
          <dd className="mono">{result.tenantId}</dd>
          {result.jobId ? (
            <>
              <dt>Job ID</dt>
              <dd className="mono">{result.jobId}</dd>
            </>
          ) : null}
        </dl>
      </Modal>
    );
  }

  return (
    <Modal
      title="Create tenant"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="create-tenant-form"
            disabled={pending || !canSubmit}
          >
            {pending ? (
              <Spinner label={mode === "lead" ? "Saving…" : "Provisioning…"} />
            ) : mode === "lead" ? (
              "Save as lead"
            ) : (
              "Create & provision"
            )}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <div className="seg" role="group" aria-label="Creation mode" style={{ marginBottom: 14 }}>
        <button
          type="button"
          className={`seg-btn${mode === "lead" ? " active" : ""}`}
          aria-pressed={mode === "lead"}
          onClick={() => setMode("lead")}
        >
          Save as lead
        </button>
        <button
          type="button"
          className={`seg-btn${mode === "provision" ? " active" : ""}`}
          aria-pressed={mode === "provision"}
          onClick={() => setMode("provision")}
        >
          Create &amp; provision now
        </button>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        {mode === "lead"
          ? "Adds the prospect to the sales pipeline without creating any resources. Provision it later from the lead's page."
          : "Immediately enqueues provisioning — D1, R2, routing and email — and emails the admin their sign-in."}
      </p>
      <form id="create-tenant-form" onSubmit={onSubmit}>
        <div className="row">
          <label className="field">
            <span className="label">Workspace slug</span>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
              placeholder="acme"
              autoFocus
              required
            />
            <span className="hint">
              {slug && !slugValid
                ? "Lowercase letters, digits and hyphens only."
                : "Becomes {slug}.actionconnect.com"}
            </span>
          </label>
          <label className="field">
            <span className="label">Organization name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Inc."
              required
            />
          </label>
        </div>

        <div className="row">
          <label className="field">
            <span className="label">{mode === "lead" ? "Contact name" : "Admin name"}</span>
            <input
              type="text"
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
              placeholder={mode === "lead" ? "Jane Prospect" : "Admin"}
            />
          </label>
          <label className="field">
            <span className="label">
              {mode === "lead" ? "Contact email" : "Admin email"}
              {mode === "provision" ? "" : " (optional)"}
            </span>
            <input
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder={mode === "lead" ? "jane@college.edu" : "admin@acme.com"}
              required={mode === "provision"}
            />
            {mode === "lead" ? (
              <span className="hint">Becomes the tenant admin when you provision the lead.</span>
            ) : null}
          </label>
        </div>

        <label className="field">
          <span className="label">Plan</span>
          <select value={planCode} onChange={(e) => setPlanCode(e.target.value)}>
            <option value="">Default (free)</option>
            {plans.map((p) => (
              <option key={p.id} value={p.code}>
                {p.name} ({p.code})
              </option>
            ))}
          </select>
          {plansLoading ? <span className="hint">Loading plans…</span> : null}
        </label>
      </form>
    </Modal>
  );
}
